import * as RadixTabs from '@radix-ui/react-tabs';
import type { ReactNode } from 'react';

interface TabDefinition {
  value: string;
  label: string;
  /** Zahl neben der Beschriftung -- etwa die Anzahl offener Teilaufgaben. */
  count?: number | undefined;
  content: ReactNode;
}

interface TabsProps {
  tabs: TabDefinition[];
  defaultValue?: string;
  ariaLabel: string;
  /** Gesteuert von aussen -- etwa, wenn ein Tastenkuerzel den Reiter
   *  wechselt. Ohne beide Angaben bleibt die Auswahl bei den Reitern. */
  value?: string;
  onValueChange?: (value: string) => void;
}

/**
 * Reiter.
 *
 * Von Radix und nicht von Hand: Pfeiltasten, `aria-selected`, die Verbindung
 * zwischen Reiter und Inhalt ueber `aria-controls` -- alles Dinge, die eine
 * eigene Umsetzung regelmaessig auslaesst und deren Fehlen erst auffaellt,
 * wenn jemand ohne Maus arbeitet.
 *
 * Der Inhalt eines nicht gewaehlten Reiters wird **nicht** ausgehaengt
 * (`forceMount` bleibt aus, aber die Abfragen laufen ueber `enabled`): Ein
 * Reiter, der bei jedem Wechsel neu laedt, fuehlt sich langsam an, und ein
 * Reiter, der im Hintergrund staendig laedt, belastet den Server ohne Not.
 *
 * **Wahlweise gesteuert.** Ohne ``value`` verwalten die Reiter ihre Auswahl
 * selbst -- das ist der Normalfall und der einfachere. Mit ``value`` und
 * ``onValueChange`` liegt sie beim Aufrufer: Ein Tastenkuerzel, das zur
 * Zeiterfassung springt, muss den Reiter von aussen umschalten koennen.
 */
export function Tabs({ tabs, defaultValue, ariaLabel, value, onValueChange }: TabsProps) {
  return (
    <RadixTabs.Root
      // Beides gleichzeitig waere ein Fehler: Radix schaltete dann zweimal
      // je Klick. Entweder gesteuert oder nicht.
      {...(value === undefined
        ? { defaultValue: defaultValue ?? tabs[0]?.value }
        : { value, onValueChange })}
      className="tabs"
    >
      <RadixTabs.List className="tabs__list" aria-label={ariaLabel}>
        {tabs.map((tab) => (
          <RadixTabs.Trigger key={tab.value} value={tab.value} className="tabs__trigger">
            {tab.label}
            {tab.count !== undefined && tab.count > 0 ? (
              <span className="tabs__count">{tab.count}</span>
            ) : null}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>

      {tabs.map((tab) => (
        <RadixTabs.Content key={tab.value} value={tab.value} className="tabs__content">
          {tab.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  );
}
